'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';
import Loader from "@uw-it-sis/lib-react/lib/Loader";

import {FaKey, FaExclamationTriangle, FaBan, FaRegFileAlt} from "react-icons/fa";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {groupBy, pluralize, isFalse, isTrue, isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import NoticesWidget from '@uw-it-sis/lib-react-myplan/lib/NoticesWidget';
import SimpleState from '@uw-it-sis/lib-react/lib/SimpleState';
import {DaysOfWeek, States} from '@uw-it-sis/lib-react/lib/AppConstants';
import registrationDateStore from '@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import RegistrationCodeLink from "@uw-it-sis/lib-react-myplan/lib/RegistrationCodeLink";

import registrationDetailsStore from './RegistrationDetailsStore';
import RegistrationButton from './RegistrationButton';
import RegisterUW from './RegisterUW';
import CoursesList from './CoursesList';
import PlanItemModal from './PlanItemModal';
import RegistrationSeamless from "./RegistrationSeamless";
import RegistrationFailure from "./RegistrationFailure";
import {PlanItemType} from "../lib/Constants";

/**
 * Creates a ListGroup of courses from academic record. Academic record is a flat list of sections vs a
 * list of courses with section data. Because of this, we are grouping sections by course id, so the
 * model will mimic the structure of a list of courses with sections.
 * @param props contains list of sections
 * @return a list of courses with sections
 */
export function AcademicRecordList(props) {
    // Create an object of courses by grouping sections by courseId
    let coursesGroup = groupBy(props.sections, 'courseId');
    let courseItems = [];

    // Iterate the object of courses and push course into the courseItems list
    Object.entries(coursesGroup).forEach(function (item, courseIndex) {
        // Create a template for course details
        let courseTemp = {
            id: null,
            code: null,
            title: null,
            credit: null,
            sections: []
        };

        // item[1] is a list of sections for the courseId
        item[1].map(function (activity, activityIndex) {
            // Populate the course details template from the first section of the list. Each section has course details.
            if (activityIndex === 0) {
                courseTemp.id = activity.courseId;
                courseTemp.code = activity.courseCode.replace(/\s\s+/g, ' ');
                courseTemp.title = activity.courseTitle;
                courseTemp.credit = activity.credit;
            }

            // If the section is not active, build a badge for displaying section state
            let enrollStatus = null;
            let stateKey = activity.activityOfferingItem.stateKey;
            if ( stateKey !== 'active' && stateKey !== null && stateKey !== '' ) {
                enrollStatus =
                    <Col xs={5} className="text-end">
                        <a href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/faqs/" target="_blank">
                            <Badge className="fs-9 text-uppercase fw-normal border border-warning">
                                {activity.activityOfferingItem.stateKey}
                            </Badge>
                        </a>
                    </Col>;
            }

            //Add class meeting times if this is being called from schedule builder
            let classMeetingTimes = null;
            if(props.view && props.view === "sb") {
                classMeetingTimes = activity.activityOfferingItem.meetingDetailsList.map((meeting, index) => {

                    // If section is has days and time, show meeting details, if not show TBA message
                    if (meeting.days && meeting.time) {
                        let meetingDays = DaysOfWeek.filter(day => meeting[day.key]).map(day => {
                            return day.shortCode;
                        });

                        let building = !meeting.building ? "" : meeting.building;
                        let location = !meeting.location ? "" : meeting.location;
                        let meetingLocation = `${building} ${location}`;

                        return (
                            <Row key={index}>
                                <Col sm={3} className="text-sm-start">{meeting.days}</Col>
                                <Col sm={6} className="text-sm-center">{meeting.time}</Col>
                                <Col sm={3} className="text-sm-end">{meetingLocation}</Col>
                            </Row>
                        );
                    } else {
                        return (
                            <Row key={index}>
                                <Col>To be arranged</Col>
                            </Row>
                        );
                    }
                });
            }

            let slnLink = <RegistrationCodeLink sln={activity.activityOfferingItem.registrationCode} qtryr={TermUtils.makeQtrYearFromTermId(activity.activityOfferingItem.termId)} className="align-middle" />;

            // If summer term, create a badge for display
            let summerBadge = null;
            if ( activity.activityOfferingItem.hasOwnProperty("summerTerm") && activity.activityOfferingItem.summerTerm ) {
                summerBadge =
                    <>
                        <span className="sr-only">Summer Term: </span>
                        <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">{activity.activityOfferingItem.summerTerm}</Badge>
                    </>
            }

            // Add section data to list of sections for the course
            courseTemp.sections.push(
                <div key={activityIndex}>
                    <Row className="flex-grow-1 d-flex align-items-center g-0">
                        <Col xs={2} className="text-nowrap"><span className="sr-only">Section </span>{activity.activityOfferingItem.code}</Col>
                        <Col xs={12} sm={8}>
                            {slnLink}
                            <span className="sr-only">Section Type: </span>
                            <Badge bg="light-gray"
                                   className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">
                                {activity.activityOfferingItem.activityOfferingType}
                            </Badge>
                            {summerBadge}
                        </Col>
                        <Col xs={2}>
                            {enrollStatus}
                        </Col>
                    </Row>
                    <Row key={activityIndex} className="flex-grow-1 d-flex align-items-center g-0">
                        <Col xs={2}></Col>
                        <Col xs={12} sm={8}>{classMeetingTimes}</Col>
                    </Row>
                </div>
            );
        });

        //Do not need to build the courseHeading and creditBadge if the modal is requested from schedule builder
        let courseHeading = props.view === "sb" ? null : <span className="course-code">{courseTemp.code}</span>;
        let creditBadge = props.view === "sb" ? null : <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2" aria-hidden="true">{courseTemp.credit} <abbr title="Credit">CR</abbr></Badge>;

        let courseClass = props.view === "sb" ? "list-group-item" : "border-start border-start-5 border-start-purple border-top py-3 ps-3 pe-0";
        let courseLink = null;

        if ( props.view !== "sb" && courseTemp.code && courseTemp.id) {
            courseHeading =
                <a href={`/course/#/courses/${courseTemp.code}?id=${courseTemp.id}`} className="course-code">
                    {courseTemp.code}
                </a>;

            courseLink =
                <a href={`/course/#/courses/${courseTemp.code}?id=${courseTemp.id}`} className="d-block lead">
                    {courseTemp.title}
                </a>;
        }

        // Add course to list of courses for academic record (aka registered courses)
        courseItems.push(
            <li key={courseIndex}
                className={courseClass}>
                <div className="mb-2">
                    <h3 className="mb-0 d-inline align-middle">
                       {courseHeading}
                    </h3>
                    {creditBadge}
                    {courseLink}
                    <span className="sr-only">{courseTemp.credit} {pluralize("Credit", courseTemp.credit)}</span>
                </div>
                {courseTemp.sections}
            </li>
        );
    });

    return (
        <ul className="list-unstyled list-add-space mb-0">
            {courseItems}
        </ul>
    );
}

class RegistrationDetails extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = registrationDetailsStore;
        this.analyticsStore = appStore.getAnalyticsStore();
        this.state = { showRegistrationResponseModal: false };
        this.enableNewRegExp = appStore.getAppAttribute("enable_new_reg_exp");
        this.toggleModal = this.toggleModal.bind(this);
        this.hideFailureModal = this.hideFailureModal.bind(this);
        this.onSqvLinkClick = this.onSqvLinkClick.bind(this);
        this.buildDoneComponent = this.buildDoneComponent.bind(this);
    }

    componentDidMount() {
        // Get plannedTerm data for termId
        this.store.mount(this.props.termId);
    }

    componentWillUnmount() {
        this.store.unmount(this.props.termId);
    }

    componentDidUpdate(prevProps) {
        if ( this.props.termId !== prevProps.termId ) {
            this.store.mount(this.props.termId);
        }
    }

    /**
     * Toggles open/close the modal response with details about sending planned items over to SPS for registration
     */
    toggleModal() {
        if ( this.state.showRegistrationResponseModal ) {
            this.analyticsStore.addEvent("RegPrev-ModalCloseClick", {}, {});
            this.store.refreshData(true);
        }
        this.setState({showRegistrationResponseModal: !this.state.showRegistrationResponseModal});
    }

    hideFailureModal() {
        this.store.clearFailureMessage();
    }

    /**
     * Adds analytics for Single Quarter View (SQV) link clicks.
     *
     */
    onSqvLinkClick() {
        this.analyticsStore.addEvent("RegPrev-SqvLinkClick", {}, {});
    }

    buildDoneComponent() {
        const propTerm = this.props.termId;
        // Show registration acknowledgement notices from the housekeeping category
        let acknowledgements;
        if ( this.store.getNoticesCount("HOUSEKEEPING") > 0 && isFalse(this.enableNewRegExp) ) {
            acknowledgements =
                <Card bg="transparent" className="border mb-4" style={{backgroundColor: '#f8f9fa'}}>
                    <Card.Header className="sr-only"><h2>Acknowledgements</h2></Card.Header>
                    <Card.Body>
                        <NoticesWidget category="HOUSEKEEPING" className="notices-prereg"/>
                    </Card.Body>
                </Card>;
        }

        // Show registration hold notices from the reg_hold category
        let notices;
        if ( this.store.getNoticesCount("REG_HOLD") > 0 ) {
            notices =
                <Card bg="transparent" className="border mb-4" role="region" aria-label="Registration Holds">
                    <Card.Header><h2 className="mb-0">Holds</h2></Card.Header>
                    <Card.Body>
                        <NoticesWidget category="REG_HOLD" icon={FaExclamationTriangle} className="notices-holds" />
                    </Card.Body>
                </Card>;
        }

        // Build UI for list of scheduled (registered) courses, if none show messaging
        let academicRecord = this.store.getAcademicRecord();
        let scheduledCourses = "You are not yet registered for any course sections";
        if ( academicRecord.length > 0 ){
            scheduledCourses = <AcademicRecordList sections={academicRecord} />;
        }

        let registeredCourses = null;
        if ( appStore.isStudent() || appStore.isAdviser() ) {
            registeredCourses =
                <Card bg="transparent" className="border mb-4" role="region" aria-label="Registered Courses">
                    <Card.Header>
                        <Card.Title>
                            <h2>Registered Courses</h2>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {scheduledCourses}
                    </Card.Body>
                    <Card.Footer>
                        <a href="https://my.uw.edu/academics" target="_blank" className="text-decoration-underline">View your Academics tab on MyUW</a>
                    </Card.Footer>
                </Card>;
        }

        // Build UI list of courses ready to register, if none show messaging
        let readyList = this.store.getFilteredPlannedList(false);
        let plannedCourses =
            <>
                <p>You do not have any course sections ready to send to Registration.</p>
                {
                    this.store.isEmpty(PlanItemType.planned) ?
                        <>
                            <p>Add sections to your planned courses to get ready.</p>
                            <p>Try <a href="/course" className="text-decoration-underline">searching for courses</a> of interest. If you know what you want, add courses directly to your Academic Year Planner.</p>
                        </>
                        :
                        <p>Resolve issues with your planned courses or try <a href="/course" className="text-decoration-underline">searching for courses</a> and adding sections to your plan.</p>
                }
            </>;
        if ( readyList.length > 0 ) {
            let regText = "The following course sections are ready to register. Send them to Registration during your registration period."
            if (isTrue(this.enableNewRegExp)) {
                regText = "The following courses are available for import to Register.UW.";
            }
            plannedCourses =
                <>
                    <p id="planned-courses-desc">{regText}</p>
                    <CoursesList courses={readyList} termId={this.props.termId} type={PlanItemType.planned} {...this.props} describedby="planned-courses-desc"/>
                </>;
        }

        let registrationAction = null;
        if ( appStore.isStudent() || appStore.isAdviser() ) {
            // if (this.seamlessRegEnabled) {
            //     if (this.store.registrationData && !isEmpty(this.store.registrationData)) {
            //         registrationAction =
            //             <RegistrationSeamless
            //                 data={this.store.registrationData}
            //                 selectionCount={this.store.getSelectedRegistrationItemsCount()}
            //                 onSuccess={() => this.store.refreshData(true)}
            //                 termId={propTerm}
            //             />
            //     } else {
            //         registrationAction =
            //             <RegistrationFailure onClose={() => this.store.refreshData(true)} isGetRegState />
            //     }
            if (isTrue(this.enableNewRegExp)) {
                registrationAction =
                    <RegisterUW
                        data={this.store.registrationData}
                        selectedTermId={propTerm}
                        hasIssues={this.store.hasIssues()}
                        isEmpty={this.store.isEmpty(PlanItemType.planned)}
                        hasReady={this.store.hasReady()}
                        readyList={this.store.getRegistrationItems()}
                    />
            } else {
                registrationAction =
                    <RegistrationButton
                        selectedTermId={propTerm}
                        hasIssues={this.store.hasIssues()}
                        isEmpty={this.store.isEmpty(PlanItemType.planned)}
                        hasReady={this.store.hasReady()}
                        readyList={this.store.getRegistrationItems()}
                        showModal={this.toggleModal}
                    />
            }
        }

        let regPreview =
            <>
                {registrationAction}
            <Card bg="transparent" className="border mb-4" key="regpreview">
                <Card.Header className="d-md-flex">
                    <Card.Title className="flex-grow-1 flex-nowrap mb-0">
                        <h2 className="mb-0">Planned</h2>
                    </Card.Title>
                </Card.Header>
                <Card.Body role="region" aria-label="Courses Ready for Registration">
                    {plannedCourses}
                </Card.Body>
                <Card.Footer>
                    {/*{registrationAction}*/}
                </Card.Footer>
            </Card>
            </>
        // Build UI list of courses not ready to register and backup courses, if none show nothing
        let issuesList = this.store.getFilteredPlannedList(true);
        let backupList = this.store.getFullBackupList();
        let issuesCard;
        let backupCard;

        let issuesDisplay;
        if (issuesList.length > 0) {
            let regText = 'The following plan items have issues you must resolve before they can be sent to Registration. See below to resolve issues with your plan items.'
            if (isTrue(this.enableNewRegExp)) {
                regText = 'The following plan items have issues you must resolve before they can be imported in Register.UW. See below to resolve issues with your planned items.'
            }
            issuesDisplay =
                <>
                    <p id="issues-list-desc">{regText}</p>
                    <CoursesList courses={issuesList} termId={this.props.termId} type={PlanItemType.planned} issues={true} {...this.props} describedby="issues-list-desc" courseTitleHeadings="h3" />
                </>;
            issuesCard =
                <Card bg="transparent" className="border mb-4" role="region" aria-label="Courses Not Ready for Registration">
                    <Card.Header className="d-md-flex">
                        <Card.Title className="flex-grow-1 flex-nowrap mb-0">
                            <h2 className="mb-0">Issues to Resolve</h2>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {issuesDisplay}
                    </Card.Body>
                </Card>;
            }

        let backupDisplay;
        if (backupList.length > 0) {
            let regText = 'Backup courses will not be sent to Registration and/or Schedule Builder. Mark them as Planned to start getting them ready to register.'
            if (isTrue(this.enableNewRegExp)) {
                regText = 'Backup courses will not be imported in Register.UW. Mark them as Planned to start getting them ready to import.'
            }
            backupDisplay =
                <>
                    <p id="backup-list-desc">{regText}</p>
                    <CoursesList courses={backupList} termId={this.props.termId} type={PlanItemType.backup} {...this.props} describedby="backup-list-desc" courseTitleHeadings="h3" />
                </>;
            backupCard =
                <Card bg="transparent" className="border mb-4" role="region" aria-label="Backup courses">
                    <Card.Header className="d-md-flex">
                        <Card.Title className="flex-grow-1 flex-nowrap mb-0">
                            <h2 className="mb-0">Backup</h2>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {backupDisplay}
                    </Card.Body>
                </Card>;
        }

        // Build a modal component for after Registration Button (over wall to SPS) action is taken
        let registrationResponseModal;
        if (this.store.getRegistrationItems() && this.store.getRegistrationItems().length > 0) {
            let sqvUrl = "/plan/#/" + TermUtils.makeTermLinkIdentifierFromTermId(propTerm);

            registrationResponseModal =
                <Modal id="modal-registration-response"
                       show={this.state.showRegistrationResponseModal}
                       onHide={this.toggleModal}
                       backdrop="static"
                       aria-labelledby="modal-registration-response-title"
                       centered>
                    <Modal.Header closeButton>
                        <Modal.Title as="h3" id="modal-registration-response-title">
                            Your selected course sections have been sent to Registration
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Your sections have been sent to Registration in a new tab.</p>
                        <p>If your registration was successful, you'll see your updated schedule when you close this window.</p>
                        <p><strong>You must carefully check your schedule in the Registration tab. If one or more of your sections has a registration error, you will not be registered for any sections.</strong></p>
                        <p>If your registration had any errors, try one of the following steps:</p>
                        <ul>
                            <li>Return to the Registration tab to see which errors need to be resolved</li>
                            <li><a href={sqvUrl}>Edit your plan</a> to change the sections that had errors</li>
                            <li>Send to Registration again from this page</li>
                        </ul>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.toggleModal}>Close and Refresh</Button>
                    </Modal.Footer>
                </Modal>;
        }

        let failureMessage;
        if ( this.store.showFailureMessageModal ) {
            failureMessage =
                <Modal id="modal-failure-message"
                       show={this.store.showFailureMessageModal}
                       onHide={this.hideFailureModal}
                       aria-labelledby="modal-failure-message-title"
                       centered>
                    <Modal.Header closeButton>
                        <Modal.Title id="modal-failure-message-title">{this.store.failureTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex align-items-start text-danger">
                            <FaBan className="me-2 mt-1" aria-hidden={true} />
                            <div>
                                {this.store.failureMessage}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={this.hideFailureModal}>Close</Button>
                    </Modal.Footer>
                </Modal>;
        }

        let warningMessage = null;
        if ( isFalse( appStore.isStudent() || appStore.isAdviser() ) ) {
            warningMessage =
                <Alert variant="danger">
                    Non-students can't register for courses until being admitted to the UW. Use this planner to prepare and plan ahead only.
                </Alert>
        }

        const loaderSpinner =
            <Spinner
                animation="dots"
                role="status">
                <span className="sr-only">Loading Your Registration...</span>
            </Spinner>;

        return (
            <Loader
                show={this.store.state === States.pending}
                message={loaderSpinner}
                contentBlur={1}
                disableDefaultStyles>
                <Row>
                    <PlanItemModal />
                    {registrationResponseModal}
                    {failureMessage}
                    <Col xs={12} md={8}>
                        <div className="d-md-none">
                            {notices}
                        </div>
                        {warningMessage}
                        {/*{*/}
                        {/*    registrationDateStore.isNowAfterPeriodOnePriorityRegDate(propTerm, 7) ?*/}
                        {/*        [regPreview, plannedDisplay]*/}
                        {/*            :*/}
                        {/*        [plannedDisplay, regPreview]*/}
                        {/*}*/}
                        {regPreview}
                        {issuesCard}
                        {backupCard}
                    </Col>
                    <Col xs={12} md={4}>
                        <div className="d-none d-md-block">
                            {notices}
                        </div>
                        {registeredCourses}
                    </Col>
                </Row>
            </Loader>
        );
    }

    render() {
        // Use 'dots' animation for first time load of course section data
        let pendingComponent = "dots";
        // If data is already present, keeping showing sections list while add/remove sections action is taking place.
        if ( this.store.hasData() ) {
            pendingComponent = this.buildDoneComponent;
        }
        return <SimpleState state={this.store.state}
                            doneComponentBuilder={this.buildDoneComponent}
                            pendingComponentBuilder={pendingComponent}
                            name="RegistrationDetails"/>
    }
}

RegistrationDetails.propTypes = {
    termId: PropTypes.string.isRequired,
    isPublished: PropTypes.bool.isRequired,
    view: PropTypes.string.isRequired
};

RegistrationDetails.defaultProps = {
    view: "rv", //default view is RegView
};

export default observer(RegistrationDetails);